import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Box,
  ListSubheader,
  Button,
  useMediaQuery,
  useTheme,
} from 'omaha-design-system';
import { IRootState } from '../../../store/reducer';
import { setSelectedSportsbooks } from '../store/slice';
import { fetchStateSportsbooks, getSportsbooksForState, StateData } from '../data/stateSportsbooksApi';
import { useSportsbookNames } from '../hooks/useSportsbookNames';
import { SportsbookLogo } from './SportsbookLogo';

function getSportsbookLogo(code: string): string {
  return `https://awesemostorage.blob.core.windows.net/assets/sportsbook/icons/${code.toLowerCase()}.png`;
}

interface SportsbookOption {
  sportsbookCode: string;
  group: string;
  operatorType: string;
}

interface PropsSportsbookDropdownProps {
  size?: 'small' | 'medium';
}

const PropsSportsbookDropdown: React.FC<PropsSportsbookDropdownProps> = ({ size = 'small' }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { filters } = useSelector((s: IRootState) => s.stokasticProps);
  const { getName, allSportsbooks } = useSportsbookNames();
  const [stateData, setStateData] = useState<StateData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  // Local state to track pending selections (only applied on close)
  const [pendingSelections, setPendingSelections] = useState<string[]>(filters.selectedSportsbooks);

  // Fetch state sportsbooks on mount (all-sportsbooks comes from the hook)
  useEffect(() => {
    fetchStateSportsbooks().then(setStateData);
  }, []);

  // Sync pending selections when filters change externally
  useEffect(() => {
    if (!isOpen) {
      setPendingSelections(filters.selectedSportsbooks);
    }
  }, [filters.selectedSportsbooks, isOpen]);

  // Get current state info
  const currentStateInfo = useMemo(() => {
    return stateData.find(s => s.abbreviation === filters.state);
  }, [stateData, filters.state]);

  // Get sportsbooks legal in the selected state
  const stateSportsbookCodes = useMemo(() => {
    return getSportsbooksForState(stateData, filters.state);
  }, [stateData, filters.state]);

  // Get ALL sportsbooks with grouping info
  const dropdownSportsbooks = useMemo(() => {
    const stateBooksSet = new Set(stateSportsbookCodes.map(s => s.toLowerCase()));
    const stateLabel = currentStateInfo?.name || filters.state;

    // Get all unique sportsbooks from the states API
    const allBooksMap = new Map<string, { code: string; operatorType: string }>();
    for (const state of stateData) {
      for (const sb of state.sportsbooks) {
        if (!allBooksMap.has(sb.sportsbookCode.toLowerCase())) {
          allBooksMap.set(sb.sportsbookCode.toLowerCase(), {
            code: sb.sportsbookCode,
            operatorType: sb.operatorType,
          });
        }
      }
    }

    // Merge in all sportsbooks (includes offshore books not linked to any state)
    for (const sb of allSportsbooks) {
      if (!allBooksMap.has(sb.sportsbookCode.toLowerCase())) {
        allBooksMap.set(sb.sportsbookCode.toLowerCase(), {
          code: sb.sportsbookCode,
          operatorType: sb.operatorType,
        });
      }
    }

    const options: SportsbookOption[] = [];
    allBooksMap.forEach((sb) => {
      const isStateBook = stateBooksSet.has(sb.code.toLowerCase());
      options.push({
        sportsbookCode: sb.code,
        group: isStateBook ? `${stateLabel} Books` : 'Other Books',
        operatorType: sb.operatorType,
      });
    });

    return options.sort((a, b) => a.sportsbookCode.localeCompare(b.sportsbookCode));
  }, [stateData, allSportsbooks, stateSportsbookCodes, currentStateInfo, filters.state]);

  // Group names
  const groups = useMemo(() => {
    return Array.from(new Set(dropdownSportsbooks.map(sb => sb.group)))
      .sort((a, b) => a === 'Other Books' ? 1 : b === 'Other Books' ? -1 : a.localeCompare(b));
  }, [dropdownSportsbooks]);

  const handleGroupSelect = (groupName: string) => {
    const groupOptions = dropdownSportsbooks
      .filter((o) => o.group === groupName)
      .map((o) => o.sportsbookCode);
    const isChecked = groupOptions.every((o) => pendingSelections?.includes(o));

    const newCodes = isChecked
      ? pendingSelections.filter(o => !groupOptions?.includes(o))
      : Array.from(new Set([...groupOptions, ...pendingSelections]));

    setPendingSelections(newCodes);
  };

  const handleChange = (sportsbookCode: string) => {
    const isSelected = pendingSelections?.includes(sportsbookCode);
    const newCodes = isSelected
      ? pendingSelections.filter(code => code !== sportsbookCode)
      : [...pendingSelections, sportsbookCode];

    setPendingSelections(newCodes);
  };

  const handleClose = () => {
    setIsOpen(false);
    // On mobile, apply changes on close (no Apply button)
    if (isMobile) {
      if (JSON.stringify([...pendingSelections].sort()) !== JSON.stringify([...filters.selectedSportsbooks].sort())) {
        dispatch(setSelectedSportsbooks(pendingSelections));
      }
    }
  };

  const handleApply = () => {
    setIsOpen(false);
    // Only dispatch if selections actually changed (create copies before sorting to avoid mutating)
    if (JSON.stringify([...pendingSelections].sort()) !== JSON.stringify([...filters.selectedSportsbooks].sort())) {
      dispatch(setSelectedSportsbooks(pendingSelections));
    }
  };

  const groupSelectAll = (groupName: string) => {
    const groupOptions = dropdownSportsbooks.filter((o) => o.group === groupName);
    const isChecked = groupOptions
      .map((o) => o.sportsbookCode)
      .every((o) => pendingSelections?.includes(o));

    return (
      <MenuItem
        key={`select-all-${groupName}`}
        value={groupName}
        onClick={(event) => {
          event.stopPropagation();
          handleGroupSelect(groupName);
        }}
      >
        <Checkbox checked={isChecked} />
        <ListItemText primary={`Select All ${groupName.includes('Books') ? '' : groupName}`} />
      </MenuItem>
    );
  };

  return (
    <FormControl size={size} sx={{ minWidth: 160 }}>
      <Select<string[]>
        multiple
        displayEmpty
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={handleClose}
        value={pendingSelections}
        renderValue={() => {
          // Always show based on applied filters, not pending
          const selected = filters.selectedSportsbooks;
          return selected.length === 0
            ? 'All Books'
            : selected.length === 1
            ? getName(selected[0])
            : `${selected.length} Books`;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 400,
              maxWidth: 375,
              '& .MuiList-root': {
                paddingRight: '8px',
              },
            },
          },
        }}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '6px',
          color: '#F2F2F3',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': { py: 1, px: 1.5 },
          '& svg': { color: '#F2F2F3' },
        }}
      >
        {/* State-specific books (first group) */}
        {groups.filter(group => group !== 'Other Books').map((group) => (
          <div key={group}>
            <ListSubheader disableSticky sx={{ bgcolor: '#1e1e20', color: '#f2f2f3b2' }}>
              {group}
            </ListSubheader>
            {groupSelectAll(group)}
            {dropdownSportsbooks
              .filter(x => x.group === group)
              .map((sportsbook) => (
                <MenuItem
                  key={sportsbook.sportsbookCode}
                  value={sportsbook.sportsbookCode}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChange(sportsbook.sportsbookCode);
                  }}
                >
                  <Checkbox checked={pendingSelections?.includes(sportsbook.sportsbookCode)} />
                  <ListItemText primary={getName(sportsbook.sportsbookCode)} />
                  <Box sx={{ ml: 1 }}>
                    <SportsbookLogo
                      book={sportsbook.sportsbookCode}
                      bookIcon={getSportsbookLogo(sportsbook.sportsbookCode)}
                      size={24}
                      borderRadius="4px"
                    />
                  </Box>
                </MenuItem>
              ))}
          </div>
        ))}

        {/* Other Books (always last) */}
        {groups.filter(group => group === 'Other Books').map((group) => (
          <div key={group}>
            <ListSubheader disableSticky sx={{ bgcolor: '#1e1e20', color: '#f2f2f3b2' }}>
              {group}
            </ListSubheader>
            {groupSelectAll(group)}
            {dropdownSportsbooks
              .filter(x => x.group === group)
              .map((sportsbook) => (
                <MenuItem
                  key={sportsbook.sportsbookCode}
                  value={sportsbook.sportsbookCode}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChange(sportsbook.sportsbookCode);
                  }}
                >
                  <Checkbox checked={pendingSelections?.includes(sportsbook.sportsbookCode)} />
                  <ListItemText primary={getName(sportsbook.sportsbookCode)} />
                  <Box sx={{ ml: 1 }}>
                    <SportsbookLogo
                      book={sportsbook.sportsbookCode}
                      bookIcon={getSportsbookLogo(sportsbook.sportsbookCode)}
                      size={24}
                      borderRadius="4px"
                    />
                  </Box>
                </MenuItem>
              ))}
          </div>
        ))}

        {/* Apply Button - Desktop only */}
        {!isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              bgcolor: '#1e1e20',
              p: 1.5,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={handleApply}
              sx={{
                height: 36,
                borderRadius: '6px',
                backgroundColor: '#42A5F5',
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '13px',
                color: 'rgba(0, 0, 0, 0.87)',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              Apply
            </Button>
          </Box>
        )}
      </Select>
    </FormControl>
  );
};

export default PropsSportsbookDropdown;
